import { supabase } from '@/integrations/supabase/client';

export interface UserEmailSettings {
  id: string;
  user_id: string;
  company_id: string;
  sendgrid_api_key: string | null;
  email_provider: 'sendgrid';
  from_name: string | null;
  from_email: string | null;
  reply_to_email: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const getUserEmailSettings = async (
  userId: string,
  companyId?: string
): Promise<UserEmailSettings | null> => {
  try {
    let query = supabase
      .from('user_email_settings')
      .select('*')
      .eq('user_id', userId);

    if (companyId) {
      query = query.eq('company_id', companyId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) {
      console.error('Error fetching user email settings:', error);
      throw error;
    }

    return data as UserEmailSettings | null;
  } catch (error) {
    console.error('Error in getUserEmailSettings:', error);
    throw error;
  }
};

export const saveUserEmailSettings = async (
  userId: string,
  companyId: string,
  settings: Partial<UserEmailSettings>
): Promise<UserEmailSettings> => {
  try {
    console.log('Saving user email settings for user:', userId);

    // Check if settings already exist
    const { data: existingSettings } = await supabase
      .from('user_email_settings')
      .select('*')
      .eq('user_id', userId)
      .eq('company_id', companyId)
      .maybeSingle();

    let dataToSave: any = {
      user_id: userId,
      company_id: companyId,
      email_provider: 'sendgrid' as const,
      from_name: settings.from_name,
      from_email: settings.from_email,
      reply_to_email: settings.reply_to_email,
      is_active: settings.is_active ?? false,
    };

    // Handle SendGrid API key encryption using user_id
    if (settings.sendgrid_api_key && settings.sendgrid_api_key !== '••••••••••••') {
      console.log('Encrypting SendGrid API key...');
      
      const { data: encryptedKey, error: encryptError } = await supabase
        .rpc('encrypt_user_api_key', { 
          api_key: settings.sendgrid_api_key,
          user_id: userId
        });

      if (encryptError || !encryptedKey) {
        console.error('Encryption error:', encryptError);
        throw new Error('Failed to encrypt SendGrid API key: ' + (encryptError?.message || 'Unknown error'));
      }

      dataToSave.sendgrid_api_key = encryptedKey;
    }

    let result;
    if (existingSettings) {
      console.log('Updating existing settings');
      const { data, error } = await supabase
        .from('user_email_settings')
        .update(dataToSave)
        .eq('user_id', userId)
        .eq('company_id', companyId)
        .select()
        .single();

      if (error) throw error;
      result = data;
    } else {
      console.log('Creating new settings');
      const { data, error } = await supabase
        .from('user_email_settings')
        .insert(dataToSave)
        .select()
        .single();

      if (error) throw error;
      result = data;
    }

    console.log('User email settings saved successfully');
    return result as UserEmailSettings;
  } catch (error: any) {
    console.error('Error saving user email settings:', error);
    throw error;
  }
};

export const testUserEmail = async (): Promise<any> => {
  try {
    const { data, error } = await supabase.functions.invoke('test-user-email');

    if (error) {
      console.error('Error testing user email:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error in testUserEmail:', error);
    throw error;
  }
};
